import { useTranslation } from "react-i18next";
import i18n from "i18next";
const t = (key: string, fallback?: string) => i18n.t(key, fallback || key);
import { CloseOutlined, SaveOutlined, UploadOutlined } from '@ant-design/icons';
import { Button, Col, Form, message, Radio, Row, Space, Typography } from 'antd';
import { isEmpty } from 'lodash';
import { useCallback, useEffect, useMemo, useState } from 'react';
import ReactQuill from 'react-quill';
import { useParams } from 'react-router-dom';

import FileUploader from '../../../../../components/file-uploader/file-uploader.component';
import FormInput from '../../../../../components/form-input/FormInput';
import { FORMAT_REACT_QUILL } from '../../../../../constants';
import { multimediaIconList } from '../../../../../constants/multimedia-constants/multimedia-constants';
import {
  createLearningMaterial,
  getLearningMaterialDetail,
  updateLearningMaterial,
} from '../../../../../service/learning-material';
import { LearningMaterialStore } from '../../../../../stores/stores';
import { notifyError } from '../../../../../utils/notification';

import DrawerCustom from '../../../../../components/drawer/Drawer';
import CustomAudioPlayer from '../../../../learning-materials/components/view/AudioPlayer';
import TextViewer from '../../../../learning-materials/components/view/TextViewer';
import VideoPlayer from '../../../../learning-materials/components/view/VideoPlayer';
import './LearningMaterialDrawer.scss';
type Props = {
  open: boolean;
  onCloseDraw: () => void;
  multimediaType: string | null;
  isEdit: any;
  learningMaterialId: any;
};

const modules = {
  toolbar: [
    ['bold', 'italic', 'underline', 'strike'],
    ['blockquote', 'code-block'],
    [{ list: 'ordered' }, { list: 'bullet' }, { indent: '-1' }, { indent: '+1' }],
    [{ size: ['small', false, 'large', 'huge'] }],
    [{ header: [1, 2, 3, 4, 5, 6, false] }],
    [{ color: [] }, { background: [] }],
    [{ font: [] }],
    [{ align: [] }],
    ['formula', 'link'],
  ],
};

const ACCEPT_BY_TYPE: Record<string, string> = {
  video: '.mp4, .mpg, .avi, .webm, .m4v',
  scorm: '.zip, .rar',
  xapi: '.zip, .rar',
  pdf: '.pdf',
  audio: '.mp3',
  mp3: '.mp3',
  text: '.txt',
};

const getAccept = (type?: string | null) => ACCEPT_BY_TYPE[type?.toLowerCase() ?? ''] ?? '';
const getS3Prefix = (type: string | null) =>
  type?.toLowerCase() === 'scorm' || type?.toLowerCase() === 'xapi' ? 'scorm' : 'sequences';

const unitModuleToLabel: Record<string, string> = {
  video: 'Video',
  scorm: 'scorm',
  xapi: 'xAPI',
  pdf: 'PDF',
  audio: 'Audio',
  mp3: 'Audio',
  text: t("Văn bản", "Văn bản"),
  reference: t('Tài liệu tham khảo', 'Tài liệu tham khảo'),
};

const getTitleByType = (type?: string | null) => unitModuleToLabel[type?.toLowerCase() ?? ''] ?? '';

const LearningMaterialDrawer: React.FC<Props> = ({
  open,
  onCloseDraw,
  multimediaType,
  isEdit,
  learningMaterialId,
}) => {
  const { t } = useTranslation();
  const { onReload } = LearningMaterialStore();
  const [form] = Form.useForm();
  const { folderId } = useParams();
  const [fileUploadUrl, setFileUploadUrl] = useState<string>('');
  const [fileListMain, setFileListMain] = useState<any[]>([]);
  const [detail, setDetail] = useState<any | null>(null);
  const [uploadComplete, setUploadComplete] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [iconType, setIconType] = useState<number>()

  const fetchLearningMaterialDetail = useCallback(async () => {
    try {
      const res = await getLearningMaterialDetail(learningMaterialId);
      setDetail(res?.data.data);
    } catch {
      notifyError(t('Có lỗi xảy ra khi lấy chi tiết học liệu', 'Có lỗi xảy ra khi lấy chi tiết học liệu'));
    }
  }, [learningMaterialId]);

  useEffect(() => {
    if (learningMaterialId && open && isEdit) fetchLearningMaterialDetail();
  }, [learningMaterialId, open, isEdit, fetchLearningMaterialDetail]);

  useEffect(() => {
    if (!(isEdit && detail && open)) return;

    form.setFieldsValue({
      title: detail?.name,
      code: detail?.code,
      description: detail?.description,
      icon: isEdit ? detail?.iconType
        : multimediaIconList?.find((it) => it.type.toLocaleLowerCase() === multimediaType)?.value,
      text: detail?.text || detail?.documentText,
      video: {
        file: {
          uid: '-1',
          name: detail?.name || 'file',
          status: 'done',
          url: detail?.mainKey,
          response: { fileKey: detail.rootKey },
        },
        fileList: [
          {
            uid: '-1',
            name: detail?.name || 'file',
            status: 'done',
            url: detail?.mainKey,
            response: { fileKey: detail.rootKey },
          },
        ],
      },
    });

    setFileUploadUrl(detail?.rootKeyUrl);
    setFileListMain([
      {
        uid: '-1',
        name: detail?.name || 'file',
        status: 'done',
        url: detail?.rootKeyUrl,
      },
    ]);
  }, [detail, isEdit, open, form]);

  const normalizedType = useMemo(() => {
    if (isEdit) return detail?.unitModule?.toLowerCase();
    return multimediaType?.toLowerCase();
  }, [isEdit, detail?.unitModule, multimediaType]);

  const iconUrl = useMemo(() => {
    if (isEdit) {
      return multimediaIconList.find((it) => it.value === detail?.iconType)?.iconUrl;
    }
    return multimediaIconList.find((it) => it.type.toLowerCase() === normalizedType)?.iconUrl;
  }, [isEdit, detail?.iconType, normalizedType]);

  const isTextMaterial = normalizedType === 'text' || normalizedType === 'reference';

  const videoFormValue = Form.useWatch('video', form);

  const titleValue = Form.useWatch('title', form);
  const textValue = Form.useWatch('text', form);

  const isSaveDisabled = useMemo(() => {
    const hasTitle = !isEmpty(titleValue);
    const hasFile = isTextMaterial
      ? !isEmpty(textValue?.replace(/<(.|\n)*?>/g, '').trim())
      : (videoFormValue?.fileList?.length ?? 0) > 0;

    return !(hasTitle && hasFile);
  }, [titleValue, textValue, videoFormValue, isTextMaterial]);

  const handleChangeFileUpload = (info: any) => {
    if (isEmpty(info.fileList)) {
      setFileUploadUrl('');
      setFileListMain([]);
      setUploadComplete(false);
      return;
    }

    const file = info.file.originFileObj;
    const type = normalizedType;

    if (info.file.status === 'done') {
      if (['video', 'audio', 'mp3', 'pdf', 'text'].includes(type)) {
        const fileUrl = URL.createObjectURL(file);
        setFileUploadUrl(fileUrl);
      } else {
        setFileUploadUrl('');
      }
      setUploadComplete(true);
    } else {
      setUploadComplete(false);
    }

    setFileListMain(info.fileList);
  };


  const renderPreview = useCallback(() => {
    if (!uploadComplete && !isEdit) return null;

    switch (normalizedType) {
      case 'video':
        return <VideoPlayer url={fileUploadUrl} />;
      case 'mp3':
      case 'audio':
        return <CustomAudioPlayer url={fileUploadUrl} />;
      case 'text':
        return <TextViewer content={detail?.documentText} />;
      case 'pdf':
        return (
          <div className="view-wrapper">
            <iframe allow="fullscreen" src={fileUploadUrl} />
          </div>
        );
      case 'scorm':
      case 'xapi':
        return (
          <iframe
            src={fileUploadUrl}
            allow="fullscreen"
            width="100%"
            height="280px"
          />
        );
      default:
        return null;
    }
  }, [uploadComplete, isEdit, normalizedType, fileUploadUrl, detail?.documentText]);

  const beforeUpload = () => false;

  const handleSave = async () => {
    setIsLoading(true);
    try {
      const values = await form.validateFields();

      const payload = {
        unitModule: isEdit ? detail?.unitModule : normalizedType,
        name: values.title,
        iconType: iconType,
        rootKey: values.video?.file.response.fileKey,
        text: values?.text,

        parentId: folderId || undefined,
        // accessLevel: Number(values.generalAccess),
        // access: Number(values.generalPermission),
        // authors: selectedUsers.map((user: any) => ({
        //   userId: user.id,
        //   permission: Number(user.permission),
        // })),
      } as any;

      if (isEdit) {
        await updateLearningMaterial(learningMaterialId, payload);
        message.success(t('Cập nhật học liệu thành công', 'Cập nhật học liệu thành công'));
      } else {
        await createLearningMaterial(payload);
        message.success(t('Tạo học liệu thành công', 'Tạo học liệu thành công'));
      }

      onReload();
      onCloseDraw();
    } catch {
      notifyError(t('Có lỗi xảy ra vui lòng thử lại sau', 'Có lỗi xảy ra vui lòng thử lại sau'));
    } finally {
      setIsLoading(false);
    }
  };

  const resetDrawerState = () => {
    form.resetFields();
    setFileListMain([])
  };

  useEffect(() => {
    if (!open) return;
    resetDrawerState();
  }, [open, learningMaterialId]);

  useEffect(() => {
    if (open) {
      if (isEdit) {
        setIconType(detail?.iconType)
      }
      else {
        setIconType(multimediaIconList?.find((item) =>
          item?.type?.toLocaleLowerCase() === multimediaType?.toLocaleLowerCase()
        )?.value)
      }
    }
  }, [open, detail, learningMaterialId])

  return (
    <DrawerCustom
      open={open}
      onClose={onCloseDraw}
      widthDrawer={736}
      title={isEdit ? `${t("Sửa học liệu", "Sửa học liệu")} ${detail?.name}` : `${t("Thêm học liệu", "Thêm học liệu")} ${getTitleByType(normalizedType)}`}
      extraNode={
        <Space>
          <Button onClick={onCloseDraw} className="btn btn-outlined">
            <CloseOutlined />
            <Typography.Text>{t("Hủy", "Hủy")}</Typography.Text>
          </Button>
          <Button
            onClick={handleSave}
            className="btn btn-primary"
            htmlType="submit"
            loading={isLoading}
            disabled={isSaveDisabled}
          >
            <SaveOutlined />
            <Typography.Text>{t("Lưu", "Lưu")}</Typography.Text>
          </Button>
        </Space>
      }
    >
      <Form
        className="add-learning-materials"
        layout="vertical"
        form={form}
        name="add-chapter"
      >
        <Row>
          <Col span={6} className="setup-time-title">
            {t("Thông tin chung", "Thông tin chung")}
          </Col>
          <Col span={18} className="learning-materials-content">
            <Form.Item
              label={t("Tiêu đề", "Tiêu đề")}
              name="title"
              rules={[{ required: true, message: t('Thông tin này bắt buộc', 'Thông tin này bắt buộc') }, { max: 255, message: t('Tiêu đề không vượt quá 255 kí tự', 'Tiêu đề không vượt quá 255 kí tự') }]}
            >
              <FormInput placeholder={t("Nhập tiêu đề", "Nhập tiêu đề")} />
            </Form.Item>
            <div className="flex align-center gap-8">
              <span>Icon</span>
              {iconUrl && <img src={iconUrl} alt="icon" style={{ width: 24, height: 24 }} />}
            </div>
          </Col>
        </Row>

        {!isTextMaterial && (
          <Row>
            <Col span={6} className="setup-time-title mt-1">
              {t("Chọn file học liệu", "Chọn file học liệu")}
            </Col>
            <Col span={18} className="learning-materials-content">
              <Radio.Group>
                <div className="radio-item">
                  <Typography.Title level={5}>{getTitleByType(normalizedType)} {t("từ thiết bị", "từ thiết bị")}</Typography.Title>
                  <Form.Item label={getTitleByType(normalizedType)} name="video" className="mb-0" rules={[{ required: true, message: t('Thông tin này bắt buộc', 'Thông tin này bắt buộc') }]}
                  >
                    <FileUploader
                      beforeUpload={beforeUpload}
                      listType="picture"
                      accept={getAccept(normalizedType)}
                      maxCount={1}
                      onChange={handleChangeFileUpload}
                      onRemove={() => setFileUploadUrl('')}
                      fileList={fileListMain}
                      prefix={getS3Prefix(normalizedType ?? '')}
                      disabled={isEdit}
                    >
                      <Button
                        disabled={isEdit}
                        icon={<UploadOutlined />}>{t("Tải", "Tải")} {getTitleByType(normalizedType)}</Button>
                    </FileUploader>
                  </Form.Item>
                  {!isEdit && normalizedType === "scorm" ? null : renderPreview()}
                </div>
              </Radio.Group>
            </Col>
          </Row>
        )}

        {isTextMaterial && (
          <Row className="mb-5 mt-4">
            <Col span={6} className="setup-time-title">
              {t("Văn bản", "Văn bản")}
            </Col>
            <Col span={18} className="learning-materials-content">
              <Form.Item name="text" rules={[{ required: true, message: t('Thông tin này bắt buộc', 'Thông tin này bắt buộc') }]}
              >
                <ReactQuill
                  readOnly={isEdit}
                  modules={modules} theme="snow" formats={FORMAT_REACT_QUILL} placeholder={t("Nhập text", "Nhập text")} className="add-learning-materials-popup" />
              </Form.Item>
            </Col>
          </Row>
        )}

      </Form>
    </DrawerCustom>
  );
};

export default LearningMaterialDrawer;
